/**
 * useTTS — Web Speech API wrapper with bilingual voice selection.
 * Falls back gracefully if the browser doesn't support speech synthesis.
 *
 * Usage:
 *   const { speak, stop, supported } = useTTS();
 *   speak('مرحباً', { lang: 'ar' });
 */
import { useCallback, useEffect, useState } from 'react';

const LANG_MAP = {
  ar: 'ar-SA',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  hi: 'hi-IN',
  ur: 'ur-PK',
  ru: 'ru-RU',
};

export function useTTS() {
  const [supported, setSupported] = useState(
    typeof window !== 'undefined' && 'speechSynthesis' in window,
  );
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    if (!supported) return;
    const update = () => setVoices(window.speechSynthesis.getVoices());
    update();
    window.speechSynthesis.addEventListener?.('voiceschanged', update);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', update);
  }, [supported]);

  const speak = useCallback((text, { lang = 'ar', rate = 0.95, pitch = 1 } = {}) => {
    if (!supported || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(String(text).trim().slice(0, 600));
      const target = LANG_MAP[lang] || lang;
      u.lang = target;
      u.rate = rate;
      u.pitch = pitch;
      const match = voices.find(v => v.lang?.toLowerCase().startsWith(target.toLowerCase()))
                 || voices.find(v => v.lang?.toLowerCase().startsWith(lang));
      if (match) u.voice = match;
      window.speechSynthesis.speak(u);
    } catch { /* never break the page on TTS error */ }
  }, [supported, voices]);

  const stop = useCallback(() => {
    if (!supported) return;
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
  }, [supported]);

  // On unmount / route change: stop talking.
  useEffect(() => () => stop(), [stop]);

  return { speak, stop, supported };
}

export default useTTS;
